import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MailboxService } from '../_services/mailbox.service';
import { MailboxError, MailboxManagedFolder, folderDisplayName } from '../_types/mailbox';

/// Diálogo para elegir qué carpetas se ven en el buzón.
///
/// La suscripción es una lista que vive en el servidor de correo y que existe precisamente para esto: decidir qué
/// carpetas ve un cliente. Sin ella, Exchange publica por IMAP carpetas que no son de correo (Calendario,
/// Contactos, Diario, Problemas de sincronización) y el árbol se llena de sitios donde no hay nada que leer. Pero
/// hay cuentas con una suscripción muy corta, que deja fuera carpetas de trabajo, y de ahí este diálogo.
///
/// El cambio se nota también en cualquier otro cliente que use la cuenta, incluido el webmail: no es una
/// preferencia local de GUAI.
///
/// Se aplica al momento, sin botón de guardar: cada interruptor es una operación independiente contra el servidor,
/// y acumularlas para enviarlas juntas solo añadiría un estado intermedio que puede quedar a medias.
@Component({
    selector: 'app-folders-manager',
    templateUrl: './folders-manager.component.html',
    styleUrls: ['./folders-manager.component.css'],
    standalone: false
})
export class FoldersManagerComponent {
  @Input() mailAccountId: string | null = null;

  @Input() set visible(value: boolean) {
    this.isVisible = value;
    if (value) this.load();
  }

  @Output() visibleChange = new EventEmitter<boolean>();

  /// Se emite cuando alguna suscripción ha cambiado, para que el árbol se recargue al cerrar.
  @Output() subscriptionsChanged = new EventEmitter<void>();

  public isVisible: boolean = false;
  public folders: MailManagedRow[] = [];
  public loading: boolean = false;
  public error: MailboxError | null = null;
  public filter: string = '';

  private dirty: boolean = false;

  constructor(private mailboxService: MailboxService) {}

  get visibleFolders(): MailManagedRow[] {
    const needle = this.filter.trim().toLowerCase();
    if (!needle) return this.folders;
    return this.folders.filter(row =>
      row.displayName.toLowerCase().includes(needle) || row.folder.path.toLowerCase().includes(needle));
  }

  get subscribedCount(): number {
    return this.folders.filter(row => row.folder.subscribed).length;
  }

  load() {
    if (!this.mailAccountId) return;

    this.loading = true;
    this.error = null;
    this.dirty = false;

    this.mailboxService.getManagedFolders(this.mailAccountId).subscribe({
      next: folders => {
        this.folders = folders.map(folder => ({
          folder,
          displayName: folderDisplayName(folder.name, folder.specialUse),
          saving: false
        }));
        this.loading = false;
      },
      error: (error: MailboxError) => {
        this.error = error;
        this.folders = [];
        this.loading = false;
      }
    });
  }

  toggle(row: MailManagedRow) {
    if (!this.mailAccountId || !row.folder.canChangeSubscription || row.saving) return;

    const target = !row.folder.subscribed;

    //se refleja al momento y se deshace si el servidor lo rechaza: así el interruptor responde sin esperar el viaje
    row.folder.subscribed = target;
    row.saving = true;

    this.mailboxService.setFolderSubscription(this.mailAccountId, row.folder.path, target).subscribe({
      next: () => {
        row.saving = false;
        this.dirty = true;
      },
      error: (error: MailboxError) => {
        row.folder.subscribed = !target;
        row.saving = false;
        this.error = error;
      }
    });
  }

  iconFor(folder: MailboxManagedFolder): string {
    switch (folder.specialUse) {
      case 'inbox': return 'pi pi-inbox';
      case 'sent': return 'pi pi-send';
      case 'drafts': return 'pi pi-file-edit';
      case 'trash': return 'pi pi-trash';
      case 'junk': return 'pi pi-ban';
      case 'archive': return 'pi pi-box';
      default: return 'pi pi-folder';
    }
  }

  close() {
    this.isVisible = false;
    this.visibleChange.emit(false);
    if (this.dirty) this.subscriptionsChanged.emit();
  }
}

/// Fila del diálogo: la carpeta más lo que necesita la interfaz.
interface MailManagedRow {
  folder: MailboxManagedFolder;
  displayName: string;
  saving: boolean;
}
